<template>
    <div
        role="img"
        :aria-label="computedAriaLabel"
        :class="[$style['mds-analyst-rating'], classObject]"
        :data-mds-version="version"
    >
        <span
            v-if="svgShield"
            v-html="svgShield"
            :style="svgShieldStyle"
            :class="rating === 'basic' ? $style['mds-analyst-rating__basic-shield'] : null"
            aria-hidden="true"
        ></span>
        <span v-html="svgLabel" :style="svgLabelStyle" aria-hidden="true"></span>
        <span v-if="quantitative" v-html="svgQuant" :style="svgQuantStyle" aria-hidden="true"></span>
    </div>
</template>

<script>
    const ariaLabels = {
        english: {
            gold: 'analyst rating gold',
            silver: 'analyst rating silver',
            bronze: 'analyst rating bronze',
            neutral: 'analyst rating neutral',
            negative: 'analyst rating negative',
            'not-ratable': 'analyst rating not ratable',
            'under-review': 'analyst rating under review',
            basic: 'analyst rating requires premium membership',
            quant: 'quantitative',
        },
        french: {
            gold: 'Note des analystes Or',
            silver: 'Note des analystes Argent',
            bronze: 'Note des analystes Bronze',
            neutral: 'Note des analystes Neutre',
            negative: 'Note des analystes Négative',
            'not-ratable': 'Note des analyses Non disponible',
            'under-review': 'Note des analystes À l’étude',
            basic: 'L’accès aux Notes requiert un abonnement Premium',
            quant: 'Quantitatif',
        },
        chinese: {
            gold: '晨星基金評級 金',
            silver: '晨星基金評級 銀',
            bronze: '晨星基金評級 銅',
            neutral: '晨星基金評級 中性',
            negative: '晨星基金評級 負面',
            'not-ratable': '晨星基金評級 不予評級',
            'under-review': '晨星基金評級 審核中',
            basic: '晨星基金評級 需要付費會員',
            quant: '量化',
        },
        'simple-chinese': {
            gold: '晨星基金评级 金',
            silver: '晨星基金评级 银',
            bronze: '晨星基金评级 铜',
            neutral: '晨星基金评级 中性',
            negative: '晨星基金评级 负面',
            'not-ratable': '晨星基金评级不予评级',
            'under-review': '晨星基金评级 审核中',
            basic: '晨星基金评级 需要高级会员',
            quant: '量化',
        },
    };

    // Heights per size (in pixels)
    const heights = {
        small: '15',
        medium: '17',
        large: '21',
    };

    // Import SVG art for all ratings
    import Svgs from './analyst-rating_svgs.json';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsAnalystRating',
        props: {
            customSize: {
                type: Number,
                docs: {
                    validation: '—',
                    description:
                        'Sets a custom height for the component in pixels. Setting a `customSize` will always override `size`.',
                },
            },
            language: {
                type: String,
                default: 'english',
                validator(value) {
                    return ['english', 'french', 'chinese', 'simple-chinese'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `english`, `french`, `chinese`, `simple-chinese`.',
                    description: 'Sets the language.',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            quantitative: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, renders the Q icon, indicating the rating is quantitative.',
                },
            },
            rating: {
                type: String,
                validator(value) {
                    return (
                        [
                            'gold',
                            'silver',
                            'bronze',
                            'neutral',
                            'negative',
                            'under-review',
                            'not-ratable',
                            'basic',
                        ].indexOf(value) !== -1
                    );
                },
                docs: {
                    validation:
                        'One of: `gold`, `silver`, `bronze`, `neutral`, `negative`, `under-review`, `not-ratable`, `basic`.',
                    description: 'Sets the rating type.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`.',
                    description: 'Sets the size of the rating.',
                },
            },
        },
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            computedAriaLabel() {
                // Combine rating aria-label with quantitative aria-label, when quantitative=true
                return `${ariaLabels[this.language][this.rating]}${this.quantLabel}`;
            },
            quantLabel() {
                return this.quantitative ? ` ${ariaLabels[this.language].quant}` : '';
            },
            svgShield() {
                return Svgs['shields'][this.rating];
            },
            svgShieldStyle() {
                return this.getStyleString(this.svgShield);
            },
            svgLabel() {
                return Svgs['labels'][this.language][this.rating];
            },
            svgLabelStyle() {
                return this.getStyleString(this.svgLabel);
            },
            svgQuant() {
                return Svgs['quants'][this.language][this.rating];
            },
            svgQuantStyle() {
                return this.getStyleString(this.svgQuant);
            },
        },
        methods: {
            getStyleString(svgString) {
                // Regex pulls viewBox from SVG string, isolates just the value group with [1]
                const viewbox = /viewBox='([^']+)'/.exec(svgString)[1];
                // Split viewBox value
                const viewBoxArray = viewbox.split(' ');
                // Calculate ratio by dividing width [2] by height [3] from viewBox values
                const ratio = viewBoxArray[2] / viewBoxArray[3];

                return this.customSize
                    ? `height: ${this.customSize}px; width: ${this.customSize * ratio}px;`
                    : `height: ${heights[this.size]}px; width: ${heights[this.size] * ratio}px;`;
            },
        },
    };
</script>

<style lang="scss" module>
    @import './analyst-rating.scss';
</style>
